import React, { useEffect, useRef, useState } from 'react';

/**
 * Google AdSense ad slot as a reusable React component.
 *
 * The loader script lives in public/index.html. This component renders the
 * <ins class="adsbygoogle"> element and pushes a single request onto the
 * global adsbygoogle queue once per mounted instance. Ads are shown to every
 * visitor (there is no ad-free tier).
 *
 * Empty-slot handling: AdSense sets `data-ad-status="filled" | "unfilled"` on
 * the <ins> once it has processed the slot. We watch that attribute and, if the
 * slot stays unfilled (e.g. on a non-approved domain, when the script is
 * blocked, or simply no ad was available), the whole unit renders nothing — so
 * no empty "Advertisement" placeholder box clutters the layout. Real ads (on
 * the approved production domain) show normally, with the small label on top.
 */
const AD_CLIENT = 'ca-pub-8333705026486949';

export const AdUnit = ({
  slot,
  format = 'auto',
  layout,
  responsive = true,
  className = '',
  style,
  testId,
  label,
}) => {
  const pushed = useRef(false);
  const insRef = useRef(null);
  // 'pending' until AdSense reports a status; 'filled' or 'unfilled' after.
  const [status, setStatus] = useState('pending');

  useEffect(() => {
    // Push the ad request exactly once per mounted instance.
    if (!pushed.current) {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        pushed.current = true;
      } catch (e) {
        // AdSense script not ready / domain not approved / blocked — ignore.
      }
    }

    const el = insRef.current;
    if (!el) return undefined;

    const readStatus = () => {
      const s = el.getAttribute('data-ad-status');
      if (s === 'filled') { setStatus('filled'); return true; }
      if (s === 'unfilled') { setStatus('unfilled'); return true; }
      return false;
    };

    if (readStatus()) return undefined;

    const observer = new MutationObserver(() => {
      if (readStatus()) observer.disconnect();
    });
    observer.observe(el, { attributes: true, attributeFilter: ['data-ad-status'] });

    // Fallback: if AdSense never reports a status (script blocked / domain not
    // approved), collapse the unit instead of leaving an empty placeholder.
    // Generous delay so a genuinely slow-loading real ad is never hidden.
    const timer = setTimeout(() => {
      if (!readStatus()) setStatus('unfilled');
      observer.disconnect();
    }, 6000);

    return () => { observer.disconnect(); clearTimeout(timer); };
  }, []);

  // Collapse unfilled slots entirely so no empty placeholder is shown.
  if (status === 'unfilled') return null;

  const insProps = {
    className: 'adsbygoogle',
    style: style || { display: 'block' },
    'data-ad-client': AD_CLIENT,
    'data-ad-slot': slot,
    'data-ad-format': format,
  };
  if (layout) insProps['data-ad-layout'] = layout;
  if (responsive) insProps['data-full-width-responsive'] = 'true';

  return (
    <div className={`text-center ${className}`} data-testid={testId}>
      {label && status === 'filled' ? (
        <span className="mb-1 block text-[10px] font-medium uppercase tracking-[0.12em] text-muted-foreground/60">
          {label}
        </span>
      ) : null}
      <ins ref={insRef} {...insProps} />
    </div>
  );
};

export default AdUnit;
